/**
 * ConfigSectionRenderer
 * Smart auto-grouping renderer for admin configuration sections
 * Groups fields by type into cards with 2-column grid layout
 */

import React from 'react';
import {
    Stack,
    Text,
    TextField,
    Toggle,
    Dropdown,
    IDropdownOption,
    DefaultButton,
    PrimaryButton,
    mergeStyles,
    TooltipHost,
} from '@fluentui/react';
import {
    ToggleLeftRegular,
    TextFieldRegular,
    NumberSymbolRegular,
    DropRegular,
    LockClosedRegular,
    DocumentTextRegular,
    SettingsRegular,
    InfoRegular,
    BrainCircuitRegular,
} from '@fluentui/react-icons';
import { FieldCard, InlineField, CredentialField, ActionButtonGroup } from './FieldCard';
import ModelManagement from './ModelManagement';
import AdminLogoUploader from '../AdminLogoUploader';
import { EntraUserPicker, EntraGroupPicker } from '../EntraUserPicker';

// Field type groupings
type FieldGroup = 'toggles' | 'selects' | 'numbers' | 'credentials' | 'endpoints' | 'text' | 'textarea' | 'actions' | 'info';

interface ConfigField {
    key: string;
    label: string;
    field_type: string;
    default_value: any;
    current_value: any;
    description: string;
    placeholder: string;
    required: boolean;
    options: Array<{ value: any; label: string }>;
    validation_rules: any;
    sensitive: boolean;
    restart_required: boolean;
    dynamic_source?: string;
    dynamic_value_field?: string;
    dynamic_label_field?: string;
}

interface ConfigSectionRendererProps {
    fields: ConfigField[];
    category: string;
    onFieldChange: (category: string, key: string, value: any) => void;
    onButtonClick?: (fieldKey: string) => Promise<void>;
    searchFilter?: string;
    configuration?: any;
    sensitiveKeysStatus?: Record<string, { has_value: boolean; source: string | null }>;
}

// Group titles and icons
const groupConfig: Record<FieldGroup, { title: string; icon: React.ReactNode }> = {
    toggles: { title: 'Feature Switches', icon: <ToggleLeftRegular style={{ fontSize: 14 }} /> },
    selects: { title: 'Options', icon: <DropRegular style={{ fontSize: 14 }} /> },
    numbers: { title: 'Numeric Settings', icon: <NumberSymbolRegular style={{ fontSize: 14 }} /> },
    credentials: { title: 'Credentials', icon: <LockClosedRegular style={{ fontSize: 14 }} /> },
    endpoints: { title: 'Service Endpoints', icon: <SettingsRegular style={{ fontSize: 14 }} /> },
    text: { title: 'Configuration', icon: <TextFieldRegular style={{ fontSize: 14 }} /> },
    textarea: { title: 'Extended Configuration', icon: <DocumentTextRegular style={{ fontSize: 14 }} /> },
    actions: { title: 'Actions', icon: <SettingsRegular style={{ fontSize: 14 }} /> },
    info: { title: 'Information', icon: <InfoRegular style={{ fontSize: 14 }} /> },
};

// Determine which group a field belongs to
function getFieldGroup(field: ConfigField): FieldGroup {
    // Info fields (read-only)
    if (field.field_type === 'info') {
        return 'info';
    }
    
    // Sensitive fields that are Key Vault managed
    if (field.sensitive && field.field_type === 'info') {
        return 'credentials';
    }
    
    // Credentials (sensitive text/password fields)
    if (field.sensitive || field.field_type === 'password') {
        return 'credentials';
    }
    
    // Endpoint URLs
    if (field.key.toLowerCase().includes('endpoint') || 
        field.key.toLowerCase().includes('url') ||
        (field.placeholder && field.placeholder.includes('https://'))) {
        return 'endpoints';
    }
    
    // Toggles
    if (field.field_type === 'boolean') {
        return 'toggles';
    }
    
    // Selects
    if (field.field_type === 'select' || field.field_type === 'dynamic_select' || field.field_type === 'multiselect') {
        return 'selects';
    }
    
    // Numbers
    if (field.field_type === 'number' || field.field_type === 'float') {
        return 'numbers';
    }
    
    // Textareas and JSON
    if (field.field_type === 'textarea' || field.field_type === 'json') {
        return 'textarea';
    }
    
    // Buttons
    if (field.field_type === 'button') {
        return 'actions';
    }
    
    // Default to text
    return 'text';
}

// Check if field matches search filter
function fieldMatchesSearch(field: ConfigField, searchFilter: string): boolean {
    if (!searchFilter) return true;
    const lower = searchFilter.toLowerCase();
    return (
        field.key.toLowerCase().includes(lower) ||
        field.label.toLowerCase().includes(lower) ||
        (field.description ? field.description.toLowerCase().includes(lower) : false)
    );
}

// Compact field styles
const compactTextFieldStyles = {
    root: { marginBottom: 0 },
    fieldGroup: {
        borderRadius: '6px',
        border: '1px solid #e1e5e9',
    },
    field: { fontSize: '13px', padding: '6px 10px' },
};

const compactToggleStyles = {
    root: { marginBottom: 0 },
    label: { fontSize: '13px' },
};

const compactDropdownStyles = {
    dropdown: {
        borderRadius: '6px',
        border: '1px solid #e1e5e9',
    },
    title: { fontSize: '13px' },
};

export const ConfigSectionRenderer: React.FC<ConfigSectionRendererProps> = ({
    fields,
    category,
    onFieldChange,
    onButtonClick,
    searchFilter = '',
    configuration,
    sensitiveKeysStatus = {},
}) => {
    const [loadingButtons, setLoadingButtons] = React.useState<Set<string>>(new Set());

    // Handle button click with loading state
    const handleButtonClick = async (fieldKey: string) => {
        if (!onButtonClick) return;
        
        setLoadingButtons(prev => new Set(prev).add(fieldKey));
        try {
            await onButtonClick(fieldKey);
        } finally {
            setLoadingButtons(prev => {
                const newSet = new Set(prev);
                newSet.delete(fieldKey);
                return newSet;
            });
        }
    };

    // Filter fields based on search
    const filteredFields = fields.filter(f => fieldMatchesSearch(f, searchFilter));
    
    // Skip hidden fields
    const visibleFields = filteredFields.filter(f => {
        // Skip legacy/hidden fields
        const hiddenKeys = [
            'CHAT_GROUNDING_MESSAGE', 'CHAT_GROUNDING_INDEXES', 'CHAT_GROUNDING_ENABLED',
            'grounding_message', 'grounding_indexes', 'grounding_enabled',
            'AZURE_SEARCH_INDEXES_JSON', 'AZURE_SEARCH_INDEXES_JSON_DEPRECATED'
        ];
        return !hiddenKeys.includes(f.key);
    });
    
    // Group fields
    const groups: Record<FieldGroup, ConfigField[]> = {
        toggles: [],
        selects: [],
        numbers: [],
        credentials: [],
        endpoints: [],
        text: [],
        textarea: [],
        actions: [],
        info: [],
    };
    
    visibleFields.forEach(field => {
        const group = getFieldGroup(field);
        groups[group].push(field);
    });
    
    // Render a single field based on its type
    const renderField = (field: ConfigField, fullWidth: boolean = false) => {
        const value = field.current_value ?? field.default_value;
        const isHighlighted = Boolean(searchFilter && fieldMatchesSearch(field, searchFilter));
        
        // Special handling for MODEL_CONFIGURATIONS - use card-based ModelManagement
        if (field.key === 'MODEL_CONFIGURATIONS') {
            const modelConfigs = (() => {
                try {
                    return typeof value === 'string' ? JSON.parse(value) : Array.isArray(value) ? value : [];
                } catch {
                    return [];
                }
            })();
            
            return (
                <FieldCard 
                    key={field.key}
                    title="Model Configurations"
                    icon={<BrainCircuitRegular style={{ fontSize: 14 }} />}
                    columns={1}
                >
                    <ModelManagement
                        value={modelConfigs}
                        onChange={(models) => onFieldChange(category, field.key, JSON.stringify(models, null, 2))}
                    />
                </FieldCard>
            );
        }
        
        // Special handling for logo upload fields
        if (category === 'ui' && ['UI_LOGO', 'UI_FAVICON', 'UI_CHAT_LOGO'].includes(field.key)) {
            return (
                <InlineField 
                    key={field.key}
                    label={field.label}
                    description={field.description}
                    fullWidth={true}
                    highlighted={isHighlighted}
                >
                    <AdminLogoUploader
                        fieldKey={field.key}
                        label={field.label}
                        onSaved={(url) => onFieldChange(category, field.key, url)}
                    />
                </InlineField>
            );
        }
        
        // Special handling for admin email fields - use EntraUserPicker
        if (field.key === 'ADMIN_EMAILS' || field.key === 'SUPER_ADMIN_EMAILS') {
            const emailArray = typeof value === 'string' 
                ? value.split(',').map((e: string) => e.trim()).filter((e: string) => e.length > 0)
                : (Array.isArray(value) ? value : []);
            
            return (
                <InlineField 
                    key={field.key}
                    label={field.label}
                    description={field.description}
                    fullWidth={true}
                    highlighted={isHighlighted}
                >
                    <EntraUserPicker
                        selectedEmails={emailArray}
                        onSelectionChange={(emails) => onFieldChange(category, field.key, emails.join(', '))}
                        label=""
                        placeholder="Search users in Azure Entra..."
                    />
                </InlineField>
            );
        }
        
        // Special handling for admin group fields - use EntraGroupPicker
        if (field.key === 'ADMIN_GROUPS') {
            const groupArray = typeof value === 'string' 
                ? value.split(',').map((g: string) => g.trim()).filter((g: string) => g.length > 0)
                : (Array.isArray(value) ? value : []);
            
            return (
                <InlineField 
                    key={field.key}
                    label={field.label}
                    description={field.description}
                    fullWidth={true}
                    highlighted={isHighlighted}
                >
                    <EntraGroupPicker
                        selectedGroupIds={groupArray}
                        onSelectionChange={(groups) => onFieldChange(category, field.key, groups.join(', '))}
                        label=""
                        placeholder="Search groups in Azure Entra..."
                    />
                </InlineField>
            );
        }
        
        switch (field.field_type) {
            case 'boolean':
                return (
                    <InlineField 
                        key={field.key} 
                        label={field.label}
                        description={field.description}
                        highlighted={isHighlighted}
                    >
                        <Toggle
                            checked={value === true}
                            onChange={(_, checked) => onFieldChange(category, field.key, checked ?? false)}
                            styles={compactToggleStyles}
                            onText="On"
                            offText="Off"
                        />
                    </InlineField>
                );
            
            case 'select':
            case 'dynamic_select':
                const getOptions = (): IDropdownOption[] => {
                    if (field.field_type === 'dynamic_select' && field.dynamic_source === 'MODEL_CONFIGURATIONS' && configuration) {
                        const modelConfigSection = configuration.sections.find((s: any) => s.category === 'model_management');
                        if (modelConfigSection) {
                            const modelConfigField = modelConfigSection.fields.find((f: any) => f.key === 'MODEL_CONFIGURATIONS');
                            if (modelConfigField?.current_value) {
                                try {
                                    const modelConfigs = typeof modelConfigField.current_value === 'string' 
                                        ? JSON.parse(modelConfigField.current_value) 
                                        : modelConfigField.current_value;
                                    if (Array.isArray(modelConfigs)) {
                                        return modelConfigs.map((config: any) => ({
                                            key: config[field.dynamic_value_field || 'model_name'],
                                            text: config[field.dynamic_label_field || 'display_name'] || config[field.dynamic_value_field || 'model_name']
                                        }));
                                    }
                                } catch (e) { /* ignore */ }
                            }
                        }
                    }
                    return field.options?.map(opt => ({ key: opt.value, text: opt.label })) || [];
                };
                
                return (
                    <InlineField 
                        key={field.key}
                        label={field.label}
                        description={field.description}
                        fullWidth={fullWidth}
                        highlighted={isHighlighted}
                    >
                        <Dropdown
                            selectedKey={value}
                            options={getOptions()}
                            onChange={(_, option) => onFieldChange(category, field.key, option?.key)}
                            styles={compactDropdownStyles}
                            placeholder={field.placeholder || 'Select...'}
                        />
                    </InlineField>
                );
            
            case 'number':
            case 'float':
                // Generate smart hint based on field key
                const getNumericHint = () => {
                    const key = field.key.toLowerCase();
                    if (key.includes('timeout')) return 'seconds';
                    if (key.includes('limit') && key.includes('token')) return 'tokens';
                    if (key.includes('max_length') || key.includes('maxlength')) return 'characters';
                    if (key.includes('threshold') || key.includes('confidence')) return '0.0-1.0';
                    if (key.includes('retry') || key.includes('retries')) return 'attempts';
                    if (key.includes('ttl') || key.includes('expir')) return 'seconds';
                    if (key.includes('interval') || key.includes('delay')) return 'ms';
                    if (key.includes('port')) return 'port number';
                    if (key.includes('count') || key.includes('max') || key.includes('limit')) return 'count';
                    if (key.includes('temperature')) return '0.0-2.0';
                    if (key.includes('top_p') || key.includes('topp')) return '0.0-1.0';
                    return null;
                };
                const numericHint = getNumericHint();
                
                return (
                    <InlineField 
                        key={field.key}
                        label={field.label}
                        description={field.description}
                        highlighted={isHighlighted}
                    >
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                            <TextField
                                type="number"
                                value={value?.toString() || ''}
                                onChange={(_, newValue) => {
                                    const num = field.field_type === 'float' 
                                        ? parseFloat(newValue || '0')
                                        : parseInt(newValue || '0');
                                    onFieldChange(category, field.key, num);
                                }}
                                styles={{
                                    ...compactTextFieldStyles,
                                    root: { width: numericHint ? 120 : 180 }
                                }}
                                placeholder={field.placeholder}
                            />
                            {numericHint && (
                                <Text variant="small" style={{ color: '#6b7280', fontStyle: 'italic' }}>
                                    {numericHint}
                                </Text>
                            )}
                        </Stack>
                    </InlineField>
                );
            
            case 'info':
                // Detect Key Vault managed fields by description, default value, or key name patterns
                const isKeyVault = field.description?.includes('Key Vault') || 
                    field.default_value?.includes('Key Vault') ||
                    field.default_value?.includes('Managed via') ||
                    (field.sensitive && field.key.toLowerCase().includes('key')) ||
                    (field.sensitive && field.key.toLowerCase().includes('secret'));
                
                // Check if this sensitive key has a value (from ENV/Key Vault)
                const keyStatus = sensitiveKeysStatus[field.key];
                const hasEnvValue = keyStatus?.has_value === true;
                const valueSource = keyStatus?.source || null;
                
                // For credential display: if it's a Key Vault type field and has a value from ENV, show as set
                // Otherwise pass the display value
                const displayValue = (isKeyVault && hasEnvValue) 
                    ? `Set via ${valueSource === 'env' ? 'ENV' : valueSource || 'config'}`
                    : (value || 'Not configured');
                
                return (
                    <CredentialField
                        key={field.key}
                        label={field.label}
                        value={displayValue}
                        isKeyVault={isKeyVault}
                        hasValue={hasEnvValue}
                        valueSource={valueSource}
                        highlighted={isHighlighted}
                    />
                );
            
            case 'password':
                return (
                    <InlineField 
                        key={field.key}
                        label={field.label}
                        description={field.description}
                        fullWidth={true}
                        highlighted={isHighlighted}
                    >
                        <TextField
                            type="password"
                            value={value || ''}
                            onChange={(_, newValue) => onFieldChange(category, field.key, newValue)}
                            styles={compactTextFieldStyles}
                            placeholder={field.placeholder || '••••••••'}
                            canRevealPassword
                        />
                    </InlineField>
                );
            
            case 'textarea':
            case 'json':
                // Detect if this is a system prompt/instruction field - use larger textarea
                const isLargeText = field.key.toLowerCase().includes('prompt') || 
                                    field.key.toLowerCase().includes('instruction') ||
                                    field.key.toLowerCase().includes('message') ||
                                    field.key.toLowerCase().includes('template');
                return (
                    <InlineField 
                        key={field.key}
                        label={field.label}
                        description={field.description}
                        fullWidth={true}
                        highlighted={isHighlighted}
                    >
                        <TextField
                            multiline
                            rows={isLargeText ? 10 : 4}
                            value={typeof value === 'object' ? JSON.stringify(value, null, 2) : (value || '')}
                            onChange={(_, newValue) => onFieldChange(category, field.key, newValue)}
                            styles={{
                                ...compactTextFieldStyles,
                                field: { 
                                    ...compactTextFieldStyles.field, 
                                    fontFamily: (field.field_type === 'json' || isLargeText) ? 'Monaco, Consolas, monospace' : 'inherit',
                                    fontSize: isLargeText ? '12px' : '14px',
                                    lineHeight: isLargeText ? '1.5' : 'inherit'
                                },
                                wrapper: {
                                    borderRadius: 6,
                                    border: '1px solid #e2e8f0'
                                }
                            }}
                            placeholder={field.placeholder}
                        />
                    </InlineField>
                );
            
            default: // text
                return (
                    <InlineField 
                        key={field.key}
                        label={field.label}
                        description={field.description}
                        fullWidth={fullWidth}
                        highlighted={isHighlighted}
                    >
                        <TextField
                            value={value || ''}
                            onChange={(_, newValue) => onFieldChange(category, field.key, newValue)}
                            styles={compactTextFieldStyles}
                            placeholder={field.placeholder}
                        />
                    </InlineField>
                );
        }
    };
    
    // Render order: toggles, selects, numbers, endpoints, text, credentials, info, textarea, actions
    const renderOrder: FieldGroup[] = ['toggles', 'selects', 'numbers', 'endpoints', 'text', 'credentials', 'info', 'textarea', 'actions'];
    
    // Special: Extract MODEL_CONFIGURATIONS to render first (before specialized model dropdowns)
    const modelConfigField = category === 'model_management' 
        ? groups.textarea.find(f => f.key === 'MODEL_CONFIGURATIONS') 
        : null;
    
    // Remove MODEL_CONFIGURATIONS from textarea group to avoid duplicate rendering
    if (modelConfigField) {
        groups.textarea = groups.textarea.filter(f => f.key !== 'MODEL_CONFIGURATIONS');
    }
    
    return (
        <Stack tokens={{ childrenGap: 0 }}>
            {/* Render MODEL_CONFIGURATIONS first for model_management */}
            {modelConfigField && renderField(modelConfigField, true)}
            
            {renderOrder.map(groupKey => {
                const groupFields = groups[groupKey];
                if (groupFields.length === 0) return null;
                
                const config = groupConfig[groupKey];
                
                // Determine columns: toggles, selects, numbers, endpoints use 2 columns
                const useGrid = ['toggles', 'selects', 'numbers', 'endpoints'].includes(groupKey);
                
                // Credentials rendered as a list
                if (groupKey === 'credentials' || groupKey === 'info') {
                    return (
                        <FieldCard key={groupKey} title={config.title} icon={config.icon} columns={1}>
                            {groupFields.map(field => renderField(field, true))}
                        </FieldCard>
                    );
                }
                
                // Textarea/JSON fields - single column, full width
                if (groupKey === 'textarea') {
                    return (
                        <FieldCard key={groupKey} title={config.title} icon={config.icon} columns={1}>
                            {groupFields.map(field => renderField(field, true))}
                        </FieldCard>
                    );
                }
                
                // Actions - left as-is for now
                if (groupKey === 'actions') {
                    return (
                        <FieldCard key={groupKey} title={config.title} icon={config.icon} columns={1}>
                            <ActionButtonGroup>
                                {groupFields.map(field => (
                                    <Stack key={field.key} tokens={{ childrenGap: 4 }}>
                                        <DefaultButton 
                                            text={loadingButtons.has(field.key) ? 'Working...' : (field.placeholder || field.label)}
                                            onClick={() => handleButtonClick(field.key)}
                                            disabled={loadingButtons.has(field.key)}
                                            styles={{
                                                root: {
                                                    minWidth: 140,
                                                    borderRadius: 6,
                                                }
                                            }}
                                        />
                                        {field.description && (
                                            <Text variant="small" style={{ color: '#6b7280' }}>
                                                {field.description}
                                            </Text>
                                        )}
                                    </Stack>
                                ))}
                            </ActionButtonGroup>
                        </FieldCard>
                    );
                }
                
                // Grid layout for toggles, selects, numbers, endpoints, text
                return (
                    <FieldCard 
                        key={groupKey} 
                        title={config.title} 
                        icon={config.icon} 
                        columns={useGrid ? 2 : 1}
                    >
                        {groupFields.map(field => renderField(field, !useGrid))}
                    </FieldCard>
                );
            })}
        </Stack>
    );
};

export default ConfigSectionRenderer;
